import { Component, ChangeDetectionStrategy, computed, inject, input } from "@angular/core";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { TooltipModule } from "primeng/tooltip";
import { RippleModule } from "primeng/ripple";

import { IsActiveMatchOptions } from "@angular/router";

import { NavRoutesService } from "src/app/navigation/nav-routes.service";
import { SidenavItem } from "../sidenav-nav.model";

/**
 * `RouterLinkActive`'s own default. Used when an item specifies no active-match
 * options so the directive matches exactly as it did before this refactor.
 * A shared constant keeps a stable reference (avoids re-triggering the directive
 * on every change-detection pass).
 */
const DEFAULT_ACTIVE_OPTIONS: { exact: boolean } | IsActiveMatchOptions = { exact: false };

/**
 * Renders a single company-scoped side-nav link from a {@link SidenavItem}.
 * Reuses the global `.yllw-sidenav__item-link` styles, so the host stays
 * transparent (`:host { display: contents }`) and the `<li>` wrapper is owned
 * by the parent section.
 */
@Component({
    selector: "app-sidenav-link",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterModule, TranslateModule, TooltipModule, RippleModule],
    templateUrl: "./sidenav-link.component.html",
    styleUrl: "./sidenav-link.component.scss",
})
export class SidenavLinkComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly navRoutes = inject(NavRoutesService);

    // =========================================================================
    // INPUTS
    // =========================================================================
    public readonly item = input.required<SidenavItem>();
    public readonly expanded = input.required<boolean>();

    // =========================================================================
    // COMPUTED - company-scoped router link
    // =========================================================================
    public readonly link = computed(() => {
        const it = this.item();
        return this.navRoutes.route(...(it.absoluteSegments ?? it.segments));
    });

    // =========================================================================
    // COMPUTED - active-match options (falls back to the directive default)
    // =========================================================================
    public readonly activeOptions = computed(() => this.item().routerLinkActiveOptions ?? DEFAULT_ACTIVE_OPTIONS);
}
